/**
 * Rejections that `onBeforePaymentAsync` raises as part of its own flow, not as failures.
 *
 * `usePaymentSection` has to reject the payment params it is holding whenever the pre-payment step
 * does not complete — that is what releases the pay button and stops the charge; leaving the promise
 * unsettled strands both. But its `catch` cannot tell "the buyer declined a prompt" from "something
 * broke", so it reported every one of them to error monitoring. Three of the rejections QuickCheckout
 * raises are ordinary outcomes: the buyer declining the discount-loss warning, a passenger form that
 * failed its own validation, and a pay-time save superseded by a newer one. None is a defect, and
 * each buyer who hit one produced an exception someone had to triage.
 *
 * The flag travels on the error rather than being matched by message, so the hook never has to know
 * which reasons QuickCheckout uses and a renamed reason cannot silently start paging anyone.
 *
 * @param {string} reason - Machine-readable reason, carried as the error message.
 * @returns {Error} An error the payment hook settles with but does not report.
 */
export function expectedPaymentCancellation(reason) {
  const error = new Error(reason);
  error.expectedPaymentCancellation = true;

  return error;
}

/**
 * Whether a rejection is an expected outcome of the pre-payment step rather than a failure.
 *
 * @param {Error} [error] - The rejection reason.
 * @returns {boolean} True when the payment flow raised it deliberately.
 */
export function isExpectedPaymentCancellation(error) {
  return Boolean(error?.expectedPaymentCancellation);
}
